import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import RefreshIcon from '@material-ui/icons/Refresh';

import { STAGE_COL_WIDTHS, useStyles } from './Styles';
import { COUNT_DASH, formatCount, formatPercent } from './formatters';
import { downloadExecutiveSummaryReport } from './report';
import {
  fetchExecCmoOptionsAPI,
  fetchExecProjectOptionsAPI,
  fetchExecSiteOptionsAPI,
  fetchExecutiveSummaryAPI,
} from './api';

/**
 * Executive Summary (Executive Summary - Functional Specs; BMS PD Enhancements
 * [Set 2] slides 2–8).
 *
 * "How many projects, campaigns and batches are completed or in-progress" —
 * the campaigns matching the chosen CMO / Site / Project ID, split into Ongoing
 * and Past sections, each campaign showing one row per stage with its Planned /
 * Completed / Ongoing batch counts and Completion %.
 *
 * Every count is derived in the middleware from Batch Status (FSD §7); nothing
 * here recomputes them. This file is the screen: filters, the two collapsible
 * sections, and the report download.
 *
 * Like Batch Analytics and the Gantt Chart this is an in-app full-screen view,
 * opened and closed by ProductDevelopment through ProjectDevNavContext, so the
 * browser URL never changes and the dashboard's state survives underneath.
 */

const ALL = 'all';
const EMPTY_RESULTS = { lastSyncedAt: null, ongoing: [], past: [] };

// A selection is either ["all"] or a list of ids. The API strips "all", so the
// two spellings of "don't filter" collapse to the same request.
const idsFor = (selection) => (selection.includes(ALL) ? [] : selection);

/**
 * One of the three checkbox dropdowns. Checking "All" clears the individual
 * boxes and vice versa — they are alternative ways of saying the same thing,
 * and leaving both checked would make the collapsed label ambiguous.
 */
const CheckboxSelect = ({ classes, label, placeholder, options, value, onChange }) => {
  const labelById = useMemo(() => {
    const map = new Map();
    options.forEach((o) => map.set(String(o.id), o.label));
    return map;
  }, [options]);

  const handleChange = (event) => {
    const next = event.target.value;
    const hadAll = value.includes(ALL);
    const hasAll = next.includes(ALL);
    if (hasAll && !hadAll) return onChange([ALL]);
    if (hasAll && next.length > 1) return onChange(next.filter((v) => v !== ALL));
    return onChange(next);
  };

  return (
    <div>
      <div className={classes.fieldLabel}>{label}</div>
      <FormControl variant="outlined" size="small" className={classes.field}>
        <Select
          multiple
          displayEmpty
          value={value}
          onChange={handleChange}
          renderValue={(selected) => {
            if (!selected.length) return <span className={classes.placeholder}>{placeholder}</span>;
            if (selected.includes(ALL)) return 'All';
            return selected.map((id) => labelById.get(String(id)) || id).join(', ');
          }}
          MenuProps={{
            getContentAnchorEl: null,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
          }}
        >
          <MenuItem value={ALL}>
            <Checkbox size="small" color="primary" checked={value.includes(ALL)} />
            <ListItemText primary="All" />
          </MenuItem>
          {options.map((o) => (
            <MenuItem key={o.id} value={String(o.id)}>
              <Checkbox size="small" color="primary" checked={value.includes(String(o.id))} />
              <ListItemText primary={o.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

const STAGE_COLUMNS = [
  'Stage',
  'Planned Batches',
  'Completed Batches',
  'Ongoing Batches',
  'Completion (%)',
];

// A cell whose value is a dash carries no number, so it is greyed back rather
// than sitting at the same weight as a real count.
const Count = ({ classes, text, extra = '', last }) => (
  <TableCell
    className={[classes.bodyCell, last ? classes.lastRowCell : '', extra,
      text === COUNT_DASH ? classes.dashCell : ''].filter(Boolean).join(' ')}
  >
    {text}
  </TableCell>
);

const StageTable = ({ classes, stages }) => (
  <div className={classes.tableWrap}>
    <Table size="small" className={classes.table}>
      <colgroup>
        {STAGE_COL_WIDTHS.map((w, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <col key={i} style={{ width: w }} />
        ))}
      </colgroup>
      <TableHead>
        <TableRow>
          {STAGE_COLUMNS.map((label) => (
            <TableCell key={label} className={classes.headCell}>{label}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {stages.map((stage, i) => {
          const last = i === stages.length - 1;
          return (
            <TableRow key={stage.collabrixCampaignStageID}>
              <TableCell
                className={[classes.bodyCell, classes.stageCell, last ? classes.lastRowCell : '']
                  .filter(Boolean).join(' ')}
              >
                {stage.stageName}
              </TableCell>
              <Count classes={classes} last={last} text={formatCount(stage.plannedBatches)} />
              <Count classes={classes} last={last} text={formatCount(stage.completedBatches)} />
              <Count classes={classes} last={last} text={formatCount(stage.ongoingBatches)} />
              <Count
                classes={classes}
                last={last}
                extra={classes.completionCell}
                text={formatPercent(stage.completionPercent)}
              />
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

const META_FIELDS = [
  ['CMO', 'cmoName'],
  ['Site / MFG Plant', 'siteMfgPlant'],
  ['Project ID', 'projectId'],
  ['Campaign ID', 'campaignId'],
];

// Each campaign is its own chevron card, keyed by its Campaign ID — never
// merged with another and never suppressed, even when a project has more than
// one campaign in the same section (FSD §5).
const CampaignBlock = ({ classes, block, expanded, onToggle }) => {
  const completed = block.campaignStatus === 'Completed';
  // Stage rows of nothing but dashes look like a failed load. They are not —
  // the campaign's batches simply have not landed in the extract yet — so say
  // so rather than leaving the reader to guess.
  const awaitingBatches =
    block.stages.length > 0 && block.stages.every((s) => !s.plannedBatches);

  return (
    <div className={classes.campaignCard}>
      <button
        type="button"
        className={classes.campaignChevron}
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} campaign ${block.campaignId}`}
      >
        {expanded ? <ExpandMoreIcon /> : <KeyboardArrowRightIcon />}
      </button>
      <div className={classes.campaignBody}>
        <div className={classes.campaignMeta}>
          {META_FIELDS.map(([label, key]) => (
            <div key={key}>
              <div className={classes.metaLabel}>{label}</div>
              <div className={classes.metaValue}>{block[key] || COUNT_DASH}</div>
            </div>
          ))}
          <div>
            <div className={classes.metaLabel}>Campaign Status</div>
            <span
              className={`${classes.statusPill} ${
                completed ? classes.statusCompleted : classes.statusOngoing
              }`}
            >
              {block.campaignStatus}
            </span>
          </div>
        </div>

        {expanded && block.stages.length > 0 && (
          <StageTable classes={classes} stages={block.stages} />
        )}
        {expanded && block.stages.length === 0 && (
          <div className={classes.cardNote}>No stages recorded for this campaign.</div>
        )}
        {expanded && awaitingBatches && (
          <div className={classes.cardNote}>
            No batches have been recorded against these stages yet.
          </div>
        )}
      </div>
    </div>
  );
};

const ExecutiveSummary = ({ onBack }) => {
  const classes = useStyles();

  // ── filters ───────────────────────────────────────────────────────────────
  const [cmoOptions, setCmoOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [cmo, setCmo] = useState([]);
  const [site, setSite] = useState([]);
  const [project, setProject] = useState([]);

  // ── results ───────────────────────────────────────────────────────────────
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);

  // Ongoing open, Past closed — the state slides 6 and 7 show on arrival.
  const [showOngoing, setShowOngoing] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const [collapsedCampaigns, setCollapsedCampaigns] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchExecCmoOptionsAPI();
      if (!cancelled) setCmoOptions(res.data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // The three dropdowns cascade, so a change clears the selections below it —
  // in the handler, not the option-loading effect, so React batches it and a CMO
  // change reloads the project options once rather than twice.
  //
  // The option-loading effects key off these, so they must change identity only
  // when the REQUEST changes: `idsFor` maps both "nothing checked" and "All" to
  // an empty list, and a fresh [] each render would refetch the same thing on
  // every keystroke of the selection. Serialising pins the identity to the value.
  const cmoKey = JSON.stringify(idsFor(cmo));
  const siteKey = JSON.stringify(idsFor(site));
  const cmoIds = useMemo(() => JSON.parse(cmoKey), [cmoKey]);
  const siteIds = useMemo(() => JSON.parse(siteKey), [siteKey]);

  const handleCmoChange = useCallback((next) => {
    setCmo(next);
    setSite([]);
    setProject([]);
  }, []);

  const handleSiteChange = useCallback((next) => {
    setSite(next);
    setProject([]);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchExecSiteOptionsAPI(cmoIds);
      if (!cancelled) setSiteOptions(res.data || []);
    })();
    return () => { cancelled = true; };
  }, [cmoIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchExecProjectOptionsAPI({ cdmoOrgIDs: cmoIds, cmoSites: siteIds });
      if (!cancelled) setProjectOptions(res.data || []);
    })();
    return () => { cancelled = true; };
  }, [cmoIds, siteIds]);

  // FSD §4 leaves the enable condition open ("Unconfirmed / Pending"); the
  // slides settle it — greyed while any dropdown is still on its placeholder
  // (slide 5, CMO chosen but not Site/Project), enabled once all three carry a
  // selection (slides 6–8).
  const canSearch = cmo.length > 0 && site.length > 0 && project.length > 0;
  const hasResults = results.ongoing.length > 0 || results.past.length > 0;

  // Search snapshots the filters it ran with. Refresh replays THAT snapshot, so
  // it re-syncs the results on screen instead of quietly turning into a new
  // search when the user has since touched a dropdown — and the report header
  // names the filters the exported numbers actually came from.
  const [applied, setApplied] = useState(null);

  const load = useCallback(async (selection) => {
    setLoading(true);
    const res = await fetchExecutiveSummaryAPI({
      cdmoOrgIDs: idsFor(selection.cmo),
      cmoSites: idsFor(selection.site),
      collabrixProjectIDs: idsFor(selection.project),
    });
    setResults(res.data);
    setApplied(selection);
    setLoading(false);
  }, []);

  const runSearch = useCallback(() => load({ cmo, site, project }), [load, cmo, site, project]);
  const refresh = useCallback(() => { if (applied) load(applied); }, [load, applied]);

  const toggleCampaign = useCallback((key) => {
    setCollapsedCampaigns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const labelsFor = (selection, options) => {
    if (!selection.length || selection.includes(ALL)) return 'All';
    const byId = new Map(options.map((o) => [String(o.id), o.label]));
    return selection.map((id) => byId.get(String(id)) || id).join(', ');
  };

  const handleGenerateReport = () => {
    if (!applied) return;
    downloadExecutiveSummaryReport(results, {
      cmo: labelsFor(applied.cmo, cmoOptions),
      site: labelsFor(applied.site, siteOptions),
      project: labelsFor(applied.project, projectOptions),
    });
  };

  const renderSection = (title, blocks, open, setOpen) => (
    <div className={classes.section}>
      <button type="button" className={classes.sectionHeader} onClick={() => setOpen(!open)}>
        {open ? <ExpandMoreIcon /> : <KeyboardArrowRightIcon />}
        <span className={classes.sectionTitle}>{title}</span>
      </button>
      {open &&
        (blocks.length ? (
          <div className={classes.sectionBody}>
            {blocks.map((block) => {
              const key = String(block.collabrixCampaignID);
              return (
                <CampaignBlock
                  key={key}
                  classes={classes}
                  block={block}
                  expanded={!collapsedCampaigns.has(key)}
                  onToggle={() => toggleCampaign(key)}
                />
              );
            })}
          </div>
        ) : (
          <div className={classes.emptyPane}>
            <Typography className={classes.emptyText}>
              No campaigns in this section for the selected filters.
            </Typography>
          </div>
        ))}
    </div>
  );

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography className={classes.title}>Executive Summary</Typography>
        <div className={classes.backButton} onClick={onBack}>
          <ChevronLeftIcon />
          <Typography variant="body1">Back</Typography>
        </div>
      </div>

      <div className={classes.filterRow}>
        <CheckboxSelect
          classes={classes}
          label="Choose CMO:"
          placeholder="Select CMO..."
          options={cmoOptions}
          value={cmo}
          onChange={handleCmoChange}
        />
        <CheckboxSelect
          classes={classes}
          label="Choose Site / MFG Plant:"
          placeholder="Select Site / MFG Plant..."
          options={siteOptions}
          value={site}
          onChange={handleSiteChange}
        />
        <CheckboxSelect
          classes={classes}
          label="Choose Project ID:"
          placeholder="Select Project ID..."
          options={projectOptions}
          value={project}
          onChange={setProject}
        />
        <div className={classes.filterActions}>
          <Button
            variant="contained"
            className={classes.searchButton}
            disabled={!canSearch || loading}
            onClick={runSearch}
          >
            Search
          </Button>
          {/* Generate Report exports the results ON SCREEN, so it follows those
              rather than the dropdowns: editing a filter without re-running
              Search must not strand the user with a report they can still see
              but no longer download. The header names `applied`, so the sheet
              can never claim filters the numbers did not come from. */}
          <Button
            variant="contained"
            className={classes.reportButton}
            disabled={loading || !applied || !hasResults}
            onClick={handleGenerateReport}
          >
            Generate Report
          </Button>
        </div>
      </div>

      {/* Nothing below the filter bar until a Search has been run (FSD §4). */}
      {loading && (
        <div className={classes.centered}>
          <CircularProgress size={28} />
        </div>
      )}

      {!loading && applied && (
        <>
          {/* One Last Synced / Refresh per screen (FSD §5). Refresh re-runs the
              same query, so both sections update together. */}
          <div className={classes.syncRow}>
            <span className={classes.syncText}>
              {results.lastSyncedAt
                ? `Last Synced @ ${moment(results.lastSyncedAt).format('DD-MMM-YYYY HH:mm:ss').toUpperCase()}`
                : 'Last Synced @ —'}
            </span>
            <Button
              className={classes.refreshButton}
              endIcon={<RefreshIcon fontSize="small" />}
              onClick={refresh}
            >
              Refresh
            </Button>
          </div>

          {hasResults ? (
            <>
              {renderSection('View Ongoing Campaigns', results.ongoing, showOngoing, setShowOngoing)}
              {renderSection('View Past Campaigns', results.past, showPast, setShowPast)}
            </>
          ) : (
            <div className={classes.emptyPane}>
              <Typography className={classes.emptyText}>
                No campaigns match the selected CMO / Site / Project ID.
              </Typography>
            </div>
          )}
        </>
      )}
    </div>
  );
};

ExecutiveSummary.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default ExecutiveSummary;
